import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  useArticleDetail,
  useArticleList,
  useManufacturers,
  useProductGroups,
} from "../../../api/articlesApi"
import { useCustomerList } from "../../../api/customersApi"
import { useProductList } from "../../../api/productsApi"
import { useRegulations } from "../../../api/regulationsApi"
import { useSubstanceList } from "../../../api/substancesApi"
import { useSupplierList } from "../../../api/suppliersApi"
import { useTemplateSettings } from "../../../api/templateSettingsApi"
import {
  ARTICLE_STATUS_VALUES,
  productStatusLabel,
  singleSelectFacet,
  STATUS_LABEL_KEYS,
} from "../../../constants/masterDataFilters"
import { useFacetOptions } from "../../../hooks/useFacetOptions"
import {
  clearUrlParams,
  stringFilterParam,
  useArrayFilterParam,
} from "../../../hooks/useUrlFilterParams"
import { regulationFilterOptions } from "../../../utils/regulationLabel"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { RelBadge } from "../../shared/RelBadge"
import { EntityListPanel } from "../EntityListPanel"
import type { MasterDataSortValue } from "../MasterDataSort"
import { useRegisterMasterDataAgentContext } from "../masterDataAgentContext"
import { useDefaultRowSelection } from "../useDefaultRowSelection"
import { useNavSeedSearch } from "../useNavSeedSearch"

/**
 * The product-group facet holds two different answers in ONE value space: a
 * real group, or "has no group at all". Group names are free text, so a group
 * can literally be called "__none__" and a bare sentinel would be
 * indistinguishable from it. Every real value therefore carries a prefix and
 * the sentinel carries none, so no group name can impersonate it.
 */
const NO_PRODUCT_GROUP_OPTION = "no-group"
const PRODUCT_GROUP_PREFIX = "group:"

function encodeProductGroup(group: string): string {
  return `${PRODUCT_GROUP_PREFIX}${group}`
}

/** null = the no-group option, string = that group's real name. */
function decodeProductGroup(option: string): string | null {
  return option === NO_PRODUCT_GROUP_OPTION
    ? null
    : option.slice(PRODUCT_GROUP_PREFIX.length)
}

/** The manufacturer facet (MC_MASTER_DATA_MANUFACTURER_FILTER): the same
 *  value/none scheme as the product-group facet above, for the same reason. */
const NO_MANUFACTURER_OPTION = "no-manufacturer"
const MANUFACTURER_PREFIX = "manufacturer:"

interface ArticleListPanelProps {
  selectedIds: Set<string>
  onToggleSelected: (id: string) => void
}

export function ArticleListPanel({
  selectedIds,
  onToggleSelected,
}: ArticleListPanelProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get("id")
  useRegisterMasterDataAgentContext("article", selectedId)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sort, setSort] = useState<MasterDataSortValue>("name_asc")

  const [statusFilter, setStatusFilter] = useArrayFilterParam(
    searchParams,
    setSearchParams,
    "status",
  )
  const [supplierFilter, setSupplierFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "supplier_id",
  )
  const [regulationFilter, setRegulationFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "regulation_id",
  )
  const [productFilter, setProductFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "product_id",
  )
  const [substanceFilter, setSubstanceFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "substance_id",
  )
  const [customerFilter, setCustomerFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "customer_id",
  )
  const [noSupplierFilter, setNoSupplierFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "no_supplier",
  )
  const [productGroupFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "product_group",
  )
  const [noProductGroupFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "no_product_group",
  )
  // Flag off, a ?manufacturer= deep link is ignored, never sent.
  const manufacturerFilterOn = useFeatureFlag(
    "MC_MASTER_DATA_MANUFACTURER_FILTER",
  )
  const [manufacturerParam] = stringFilterParam(
    searchParams,
    setSearchParams,
    "manufacturer",
  )
  const [noManufacturerParam] = stringFilterParam(
    searchParams,
    setSearchParams,
    "no_manufacturer",
  )
  const manufacturerFilter = manufacturerFilterOn ? manufacturerParam : null
  const noManufacturerActive =
    manufacturerFilterOn && noManufacturerParam === "1"

  const supplierFacet = useFacetOptions(useSupplierList, (s) => ({
    value: s.id,
    label: s.name,
  }))
  const productFacet = useFacetOptions(useProductList, (p) => ({
    value: p.id,
    label: p.name,
  }))
  const substanceFacet = useFacetOptions(useSubstanceList, (s) => ({
    value: s.id,
    label: s.name,
  }))
  const customerFacet = useFacetOptions(useCustomerList, (c) => ({
    value: c.id,
    label: c.name,
  }))
  const { data: regulations } = useRegulations()
  const { data: productGroups } = useProductGroups()
  const { data: manufacturers } = useManufacturers(manufacturerFilterOn)
  const { data: templateSettings } = useTemplateSettings()
  const productGroupLabel =
    templateSettings?.productGroupLabel ||
    translate("MC_MD_FIELD_PRODUCT_GROUP")

  const noSupplierActive = noSupplierFilter === "1" ? noSupplierFilter : null
  const noProductGroupActive =
    noProductGroupFilter === "1" ? noProductGroupFilter : null
  // The value facet and the "no group" option are mutually exclusive answers
  // to the same question, so they share one single-select control. Both
  // params are written in ONE setSearchParams call (mirrors clearUrlParams'
  // own comment above): two separate stringFilterParam setters in the same
  // handler tick would each build off the same pre-click searchParams
  // snapshot, so only the last one would survive.
  const productGroupSelection = noProductGroupActive
    ? NO_PRODUCT_GROUP_OPTION
    : productGroupFilter
      ? encodeProductGroup(productGroupFilter)
      : null
  function setProductGroupSelection(option: string | null) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      const group = option === null ? null : decodeProductGroup(option)
      if (option === NO_PRODUCT_GROUP_OPTION) {
        params.set("no_product_group", "1")
        params.delete("product_group")
      } else if (group) {
        params.set("product_group", group)
        params.delete("no_product_group")
      } else {
        params.delete("product_group")
        params.delete("no_product_group")
      }
      return params
    })
  }

  // Same single-write rule as the product group above.
  const manufacturerSelection = noManufacturerActive
    ? NO_MANUFACTURER_OPTION
    : manufacturerFilter
      ? `${MANUFACTURER_PREFIX}${manufacturerFilter}`
      : null
  function setManufacturerSelection(option: string | null) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      const value =
        option === null || option === NO_MANUFACTURER_OPTION
          ? null
          : option.slice(MANUFACTURER_PREFIX.length)
      if (option === NO_MANUFACTURER_OPTION) {
        params.set("no_manufacturer", "1")
        params.delete("manufacturer")
      } else if (value) {
        params.set("manufacturer", value)
        params.delete("no_manufacturer")
      } else {
        params.delete("manufacturer")
        params.delete("no_manufacturer")
      }
      return params
    })
  }

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = useArticleList({
    q: debouncedSearch || undefined,
    supplier_id: supplierFilter ?? undefined,
    product_id: productFilter ?? undefined,
    customer_id: customerFilter ?? undefined,
    regulation_id: regulationFilter ?? undefined,
    substance_id: substanceFilter ?? undefined,
    status: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
    no_supplier: noSupplierActive ? true : undefined,
    product_group: productGroupFilter ?? undefined,
    no_product_group: noProductGroupActive ? true : undefined,
    manufacturer: manufacturerFilter ?? undefined,
    no_manufacturer: noManufacturerActive ? true : undefined,
    sort,
    limit: 50,
  })

  const rows = data?.pages.flatMap((p) => p.items) ?? []

  useDefaultRowSelection(rows, selectedId, setSearchParams)

  const isNavRedirect = searchParams.get("source") === "nav"
  const { data: seedDetail, isError: seedError } = useArticleDetail(
    isNavRedirect ? selectedId : null,
  )
  useNavSeedSearch({
    isNavRedirect,
    selectedId,
    detailLoaded: !!seedDetail,
    detailErrored: seedError,
    seedSearchValue: seedDetail
      ? (seedDetail.name ?? seedDetail.articleNumber ?? "")
      : undefined,
    setSearch,
    setSearchParams,
  })

  function selectRow(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", id)
      return next
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <EntityListPanel
        agentTarget="articles.list"
        filterTargetKey="articles.filters"
        rows={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onFetchMore={fetchNextPage}
        selectedId={selectedId}
        onSelectRow={selectRow}
        selectedIds={selectedIds}
        onToggleSelected={onToggleSelected}
        totalCount={data?.pages[0]?.total}
        sort={sort}
        onSortChange={setSort}
        hasFlag={(row) =>
          row.importFlags != null && Object.keys(row.importFlags).length > 0
        }
        search={search}
        onSearchChange={setSearch}
        searchEntityLabelKey="MC_MD_TAB_ARTICLES"
        onClearAllFilters={() =>
          clearUrlParams(setSearchParams, [
            "supplier_id",
            "regulation_id",
            "product_id",
            "substance_id",
            "customer_id",
            "status",
            "no_supplier",
            "product_group",
            "no_product_group",
            "manufacturer",
            "no_manufacturer",
          ])
        }
        facets={[
          {
            key: "supplier",
            title: translate("MC_MD_FILTER_SUPPLIER_TITLE"),
            ...supplierFacet,
            ...singleSelectFacet(supplierFilter, setSupplierFilter),
          },
          {
            key: "framework",
            title: translate("MC_MD_FILTER_FRAMEWORK_TITLE"),
            options: regulationFilterOptions(regulations ?? [], translate),
            ...singleSelectFacet(regulationFilter, setRegulationFilter),
          },
          {
            key: "product",
            title: translate("MC_MD_FILTER_PRODUCT_TITLE"),
            ...productFacet,
            ...singleSelectFacet(productFilter, setProductFilter),
          },
          {
            key: "substance",
            title: translate("MC_MD_FILTER_SUBSTANCE_TITLE"),
            ...substanceFacet,
            ...singleSelectFacet(substanceFilter, setSubstanceFilter),
          },
          {
            key: "customer",
            title: translate("MC_MD_FILTER_CUSTOMER_TITLE"),
            ...customerFacet,
            ...singleSelectFacet(customerFilter, setCustomerFilter),
          },
          {
            key: "status",
            title: translate("MC_MD_FILTER_STATUS_TITLE"),
            options: ARTICLE_STATUS_VALUES.map((value) => ({
              value,
              label: translate(STATUS_LABEL_KEYS[value]),
            })),
            selected: statusFilter,
            onSelectionChange: setStatusFilter,
          },
          {
            key: "no_supplier",
            title: translate("MC_MD_FILTER_NO_SUPPLIER_TITLE"),
            options: [
              {
                value: "1",
                label: translate("MC_MD_FILTER_NO_SUPPLIER_OPTION"),
              },
            ],
            ...singleSelectFacet(noSupplierActive, setNoSupplierFilter),
          },
          {
            key: "product_group",
            title: translate("MC_MD_FILTER_PRODUCT_GROUP_TITLE", {
              label: productGroupLabel,
            }),
            options: [
              {
                value: NO_PRODUCT_GROUP_OPTION,
                label: translate("MC_MD_FILTER_NO_PRODUCT_GROUP_OPTION", {
                  label: productGroupLabel,
                }),
              },
              ...(productGroups ?? []).map((g) => ({
                value: encodeProductGroup(g.value),
                label: `${g.value} (${g.count})`,
              })),
            ],
            ...singleSelectFacet(
              productGroupSelection,
              setProductGroupSelection,
            ),
          },
          ...(manufacturerFilterOn
            ? [
                {
                  key: "manufacturer",
                  title: translate("MC_MD_FIELD_MANUFACTURER"),
                  options: [
                    {
                      value: NO_MANUFACTURER_OPTION,
                      label: translate("MC_MD_FILTER_NO_MANUFACTURER_OPTION"),
                    },
                    ...(manufacturers ?? []).map((m) => ({
                      value: `${MANUFACTURER_PREFIX}${m.value}`,
                      label: `${m.value} (${m.count})`,
                    })),
                  ],
                  ...singleSelectFacet(
                    manufacturerSelection,
                    setManufacturerSelection,
                  ),
                },
              ]
            : []),
        ]}
        renderContent={(row) => (
          <EntityListRowContent
            title={row.name}
            meta={
              <>
                <span className="font-mono">{row.articleNumber}</span>
                {/* Which supplier it is bought from: two articles can share a
                    name (one per supplier) and only this tells them apart.
                    `supplierName` is the backend's primary pick (an active link
                    first); `suppliers` is name-sorted and may lead with an
                    inactive one. */}
                {row.suppliers.length > 0 && (
                  <span className="truncate">
                    {row.supplierName ?? row.suppliers[0].name}
                    {row.suppliers.length > 1 &&
                      ` +${row.suppliers.length - 1}`}
                  </span>
                )}
              </>
            }
            badges={
              <>
                <RelBadge
                  label={translate("MC_MD_REL_LABEL_SUPPLIERS")}
                  labelOne={translate("MC_MD_REL_LABEL_SUPPLIERS_ONE")}
                  count={row.suppliers.length}
                  tone="muted"
                  items={row.suppliers.slice(0, 4).map((s) => ({
                    id: s.id,
                    primary: s.name,
                    secondary: s.supplierArticleNumber ?? undefined,
                  }))}
                  itemHref={(id) => `../suppliers?id=${id}&source=nav`}
                  onShowAll={() =>
                    navigate(`../suppliers?article_id=${row.id}`)
                  }
                />
                <RelBadge
                  label={translate("MC_MD_REL_LABEL_SUBSTANCES")}
                  labelOne={translate("MC_MD_REL_LABEL_SUBSTANCES_ONE")}
                  count={row.substancesDeclaredCount}
                  tone={row.restrictedSubstancesCount > 0 ? "danger" : "muted"}
                  items={row.relSubstances.map((s) => ({
                    id: s.id,
                    primary: s.name,
                  }))}
                  itemHref={(id) => `../substances?id=${id}&source=nav`}
                  onShowAll={() =>
                    navigate(`../substances?article_id=${row.id}`)
                  }
                />
                <RelBadge
                  label={translate("MC_MD_REL_LABEL_PRODUCTS")}
                  labelOne={translate("MC_MD_REL_LABEL_PRODUCTS_ONE")}
                  count={row.productsCount}
                  tone="muted"
                  items={row.relProducts.map((p) => ({
                    id: p.id,
                    primary: p.name,
                    secondary: productStatusLabel(p.status, translate),
                  }))}
                  itemHref={(id) => `../products?id=${id}&source=nav`}
                  onShowAll={() => navigate(`../products?article_id=${row.id}`)}
                />
                <RelBadge
                  label={translate("MC_MD_REL_LABEL_CUSTOMERS")}
                  labelOne={translate("MC_MD_REL_LABEL_CUSTOMERS_ONE")}
                  count={row.customersCount}
                  tone="muted"
                  items={row.relCustomers.map((c) => ({
                    id: c.id,
                    primary: c.name,
                  }))}
                  itemHref={(id) => `../customers?id=${id}&source=nav`}
                  onShowAll={() =>
                    navigate(`../customers?article_id=${row.id}`)
                  }
                />
              </>
            }
          />
        )}
      />
    </div>
  )
}
